import { useMutation } from '@apollo/client';
import DateTimeCountDownLabel from '@monorepo/common/component/DateTimeCountDownLabel';
import DateTimeLabel from '@monorepo/common/component/DateTimeLabel';
import MoneyLabel from '@monorepo/common/component/MoneyLabel';
import PeriodLongLabel from '@monorepo/common/component/PeriodLongLabel';
import StampTypeLabel from '@monorepo/common/component/StampTypeLabel';
import TokenNameLabel from '@monorepo/common/component/token/TokenNameLabel';
import React, { MouseEventHandler, ReactNode, useCallback } from 'react';
import { Alert, Button, Col, Row, Table } from 'reactstrap';
import { GoToGatewayDocument, OrderTotalStatusEnum, StampTypeEnum } from '../../graphql-operations';
import ClientNameLink from '../client/ClientNameLink';

interface Order {
  __typename?: 'OrderTotal';
  id: string;
  orderNumber: string;
  created: string;
  validBefore: string;
  totalPriceDiscounted: number;
  status: OrderTotalStatusEnum;
  personFrom: {
    __typename?: 'Person';
    id: string;
    personData: {
      __typename?: 'PersonData';
      id: string;
      degreeBefore?: string | null;
      degreeAfter?: string | null;
      firstName: string;
      lastName: string;
    };
  };
  ordersDiscipline: Array<{
    __typename?: 'OrderDiscipline';
    id: string;
    priceOrig: number;
    priceDiscounted: number;
    covidAccount: number;
    client: {
      __typename?: 'Client';
      id: string;
      personData: {
        __typename?: 'PersonData';
        id: string;
        degreeBefore?: string | null;
        degreeAfter?: string | null;
        firstName: string;
        lastName: string;
      };
    };
    disciplineConfig: {
      __typename?: 'DisciplineConfig';
      id: string;
      period: { __typename?: 'Period'; id: string; year: number; number: number };
      discipline: { __typename?: 'Discipline'; id: string; name: string };
    };
  }>;
  ordersStamp: Array<{
    __typename?: 'OrderStamp';
    id: string;
    type: StampTypeEnum;
    priceOrig: number;
    priceDiscounted: number;
    client: {
      __typename?: 'Client';
      id: string;
      personData: {
        __typename?: 'PersonData';
        id: string;
        degreeBefore?: string | null;
        degreeAfter?: string | null;
        firstName: string;
        lastName: string;
      };
    };
    periodAppConfig: {
      __typename?: 'PeriodAppConfig';
      id: string;
      period: { __typename?: 'Period'; id: string; year: number };
    };
  }>;
  ordersToken: {
    id: string;
    client: {
      __typename?: 'Client';
      id: string;
      personData: {
        __typename?: 'PersonData';
        id: string;
        degreeBefore?: string | null;
        degreeAfter?: string | null;
        firstName: string;
        lastName: string;
      };
    };
    token: {
      id: string;
      period: { __typename?: 'Period'; id: string; year: number; number: number };
      name: string;
    };
    price: number;
  }[];
}

interface InfoRowProps {
  label: string;
  children: ReactNode;
}

const InfoRow = ({ label, children }: InfoRowProps) => {
  return (
    <Row className="mb-1">
      <Col xs={4} sm={3}>
        <span className="text-muted">{label}</span>
      </Col>
      <Col xs={8} sm={9}>
        {children}
      </Col>
    </Row>
  );
};

interface OrdersTableProps {
  order: Order;
}

function OrdersTable({ order }: OrdersTableProps) {
  const hasCovidDiscount = order.ordersDiscipline.some(od => od.covidAccount !== 0);

  const hasDiscount =
    order.ordersStamp.some(os => os.priceDiscounted !== os.priceOrig) ||
    order.ordersDiscipline.some(od => od.priceDiscounted !== od.priceOrig);

  return (
    <Table size="sm" striped>
      <thead>
        <tr>
          <th>Období</th>
          <th>Oddíl / známka</th>
          <th>Pro</th>

          {hasDiscount && (
            <>
              <th className="text-right">Částka bez slevy</th>
              <th className="text-right">Částka po slevě</th>
            </>
          )}

          {!hasDiscount && <th className="text-right">Částka</th>}

          {hasCovidDiscount && <th className="text-right">Covid účet</th>}
        </tr>
      </thead>
      <tbody>
        {/*orders stamp*/}
        {order.ordersStamp.map(orderStamp => (
          <tr key={orderStamp.id}>
            <td>{orderStamp.periodAppConfig.period.year}</td>
            <td>
              {/*@ts-ignore*/}
              <StampTypeLabel type={orderStamp.type} />
            </td>
            <td>
              <ClientNameLink client={orderStamp.client} />
            </td>

            {hasDiscount && (
              <td className="text-right">
                <MoneyLabel amount={orderStamp.priceOrig} />
              </td>
            )}

            <td className="text-right">
              <MoneyLabel amount={orderStamp.priceDiscounted} />
            </td>
            {hasCovidDiscount && <td />}
          </tr>
        ))}

        {/* orders token */}
        {order.ordersToken.map(orderToken => (
          <tr key={orderToken.id}>
            <td>
              <PeriodLongLabel period={orderToken.token.period} />
            </td>
            <td>
              <TokenNameLabel token={orderToken.token} />
            </td>
            <td>
              <ClientNameLink client={orderToken.client} />
            </td>

            {hasDiscount && (
              <td className="text-right">
                <MoneyLabel amount={orderToken.price} />
              </td>
            )}

            <td className="text-right">
              <MoneyLabel amount={orderToken.price} />
            </td>
            {hasCovidDiscount && <td />}
          </tr>
        ))}

        {/*orders discipline*/}
        {order.ordersDiscipline.map(orderDiscipline => (
          <tr key={orderDiscipline.id}>
            <td>
              <PeriodLongLabel period={orderDiscipline.disciplineConfig.period} />{' '}
            </td>
            <td>{orderDiscipline.disciplineConfig.discipline.name}</td>
            <td>
              <ClientNameLink client={orderDiscipline.client} />
            </td>
            {hasDiscount && (
              <td className="text-right">
                <MoneyLabel amount={orderDiscipline.priceOrig} />
              </td>
            )}
            <td className="text-right">
              <MoneyLabel amount={orderDiscipline.priceDiscounted} />
            </td>
            {hasCovidDiscount && (
              <td className="text-right">
                {orderDiscipline.covidAccount !== 0 && (
                  <MoneyLabel amount={orderDiscipline.covidAccount} signed={true} />
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="font-weight-bold">
          <td colSpan={hasDiscount ? 4 : 3}>Celkem</td>
          <td className="text-right">
            <MoneyLabel amount={order.totalPriceDiscounted} />
          </td>
          {hasCovidDiscount && <td />}
        </tr>
      </tfoot>
    </Table>
  );
}

export function colorForStatus(status: OrderTotalStatusEnum) {
  switch (status) {
    case 'UNPAID':
    case 'PAYING':
      return '';
    case 'PAID':
      return 'success';
    case 'REFUNDING':
    case 'REFUNDED':
      return 'warning';
    case 'EXPIRED':
      return 'danger';
    default:
  }
}

interface PaymentCsobProps {
  price: number;
  orderId: string;
}

function PaymentCsob({ price, orderId }: PaymentCsobProps) {
  const [goToGateway] = useMutation(GoToGatewayDocument);

  const onGoToGatewayClick = useCallback<MouseEventHandler>(
    e => {
      e.preventDefault();
      goToGateway({
        variables: {
          orderId
        }
      }).then(({ data }) => data && (window.location.href = data.goToGateway));
    },
    [goToGateway, orderId]
  );

  return (
    <Button color="primary" onClick={onGoToGatewayClick}>
      Zaplatit <MoneyLabel amount={price} /> kartou
    </Button>
  );
}

interface PaymentProps {
  order: Order;
}

function Payment({ order }: PaymentProps) {
  const { status, totalPriceDiscounted, validBefore } = order;
  if (status === 'UNPAID' || status === 'PAYING') {
    return (
      <>
        <hr />

        <Row>
          <Col sm={12}>
            <h4>Platba</h4>
          </Col>
          <Col sm={12} className="mb-1">
            <Alert color="warning">
              Na zaplacení máte{' '}
              <DateTimeCountDownLabel
                className="font-weight-bold"
                dateTimeString={validBefore}
                finishedLabel="Čas vypršel"
              />
            </Alert>
          </Col>
          <Col sm={12}>
            <PaymentCsob price={totalPriceDiscounted} orderId={order.id} />
          </Col>
        </Row>
      </>
    );
  }
  return null;
}

interface OrderStatusProps {
  status: OrderTotalStatusEnum;
}

function OrderStatus({ status }: OrderStatusProps) {
  const color = colorForStatus(status);

  switch (status) {
    case 'PAID':
      return <Alert color={color}>Objednávka byla zaplacena</Alert>;
    case 'REFUNDING':
      return <Alert color={color}>Objednávka byla stornována a peníze jsou na cestě zpět na Váš účet.</Alert>;
    case 'REFUNDED':
      return <Alert color={color}>Objednávka byla stornována a peníze již máte zpět.</Alert>;
    case 'EXPIRED':
      return <Alert color={color}>Čas na zaplacení vypršel</Alert>;
    case 'UNPAID':
    case 'PAYING':
    default:
      return null;
  }
}

interface OrderDetailViewProps {
  order: Order;
}

export default function OrderDetailView({ order }: OrderDetailViewProps) {
  const expired = order.status === 'EXPIRED';

  return (
    <>
      {/* header with name and New icon */}
      <Row>
        <Col>
          <h2>Objednávka č. {order.orderNumber}</h2>
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <OrderStatus status={order.status} />
        </Col>
      </Row>

      <InfoRow label="Vytvořeno">
        <DateTimeLabel dateTimeString={order.created} />
      </InfoRow>

      {(order.status === 'UNPAID' || order.status === 'PAYING' || order.status === 'EXPIRED') && (
        <InfoRow label="Platnost do">
          <DateTimeLabel
            dateTimeString={order.validBefore}
            className={`font-weight-bold ${expired ? 'text-danger' : ''}`}
          />
        </InfoRow>
      )}

      <Row>
        <Col sm={12} className="mt-3">
          <OrdersTable order={order} />
        </Col>
      </Row>

      <Payment order={order} />
    </>
  );
}
